import forge from 'apis/forge';
import { useCallback, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectActiveScene, selectFrame, selectUser } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { frameShareUrl } from 'utils';
import ShareEntityModal, { ShareEntityModalConfig } from '../ShareEntityModal';
import { selectIsMobile } from 'redux/mode/reducers';

interface ShareFrameModalProps {
	frameId: string;
	onClose: () => void;
}

const ShareFrameModal = ({ frameId, onClose }: ShareFrameModalProps) => {
	const { frame, scene, user, isMobile } = useSelector((r: RootReducer) => {
		return {
			frame: selectFrame(r.db, frameId),
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			user: selectUser(r.db),
			isMobile: selectIsMobile(r.mode),
		};
	}, shallowEqual);

	const [publiclyReadable, setPubliclyReadable] = useState(!!frame?.metadata?.publiclyReadable);

	const canNativeShare =
		isMobile && typeof navigator !== 'undefined' && typeof navigator.share === 'function';

	const close = useCallback(() => {
		onClose();
	}, [onClose]);

	const onAccessChange = useCallback(
		async (newPubliclyReadable: boolean) => {
			if (!frame) {
				toast.error('Internal error: frame not found');
				return;
			}
			if (frame.viewOnly) {
				toast.warning('You cannot change sharing settings in view-only mode');
				return;
			}
			try {
				await forge.updateFrame(
					{
						update: {
							id: frameId,
							metadata: {
								...frame.metadata,
								publiclyReadable: newPubliclyReadable,
							},
						},
					},
					true
				);
				toast.success('Prototype access updated');
				setPubliclyReadable(newPubliclyReadable);
			} catch (error) {
				toast.error('Failed to update prototype access settings');
				console.error('Error updating frame access:', error);
			}
		},
		[frame, frameId]
	);

	const config: ShareEntityModalConfig = useMemo(
		() => ({
			title: 'Share this prototype',
			entityLabel: 'prototype',
			publiclyReadable,
			showPrivateOption: !frame?.viewOnly,
			isPubliclyReadableForced: false,
			canManageAccess: !frame?.viewOnly && !!scene,
			shareUrl: frame ? frameShareUrl(frame) : '',
			shareTitle: frame?.title ?? 'Prototype',
			canNativeShare,
			trackAction: canNativeShare ? 'share_frame_link' : 'copy_frame_link',
			onAccessChange,
			onClose: close,
			accessManagerProps: {
				title: 'Manage Project Editors',
				onClose: close,
				copyLink: frame
					? {
							shareUrl: frameShareUrl(frame),
							linkLabel: canNativeShare ? 'Share prototype link' : 'Copy prototype link',
							canNativeShare,
							shareTitle: frame.title ?? 'Prototype',
							trackAction: canNativeShare ? 'share_frame_link' : 'copy_frame_link',
					  }
					: undefined,
				userAccess: {
					entityId: scene?.id ?? '',
					getUsers: async (id) => forge.getSceneUsers({ sceneId: id }),
					grantAccess: async (id, emails) =>
						forge.grantSceneAccess.withToast()({ sceneId: id, emails }),
					revokeAccess: async (id, email) =>
						forge.revokeSceneAccess.withToast()({ sceneId: id, email }),
					errorMessages: {
						fetchFailed: 'Failed to fetch scene users',
						revokeFailed: 'Failed to revoke user access',
					},
					onAccessGranted: () => {
						if (user?.id) {
							forge.updateUser({
								update: {
									id: user.id,
									metadata: { lastSharedDate: new Date().toISOString() },
								},
							});
						}
					},
				},
				organizationAccess: scene
					? {
							entityId: scene.id,
							getOrganizations: (id) => forge.getSceneOrganizations(id),
							addToOrganization: (entityId, orgId) =>
								forge.addSceneToOrganization.withToast()(entityId, orgId),
							removeFromOrganization: (entityId, orgId) =>
								forge.removeSceneFromOrganization.withToast()(entityId, orgId),
							emptyStateMessage: 'This project is not shared with any teams',
							fetchFailedMessage: 'Failed to load scene teams',
					  }
					: undefined,
			},
		}),
		[publiclyReadable, frame, scene, canNativeShare, onAccessChange, close, user?.id]
	);

	return <ShareEntityModal config={config} />;
};

export default ShareFrameModal;
